import { Component, ElementRef, EventEmitter, HostBinding, Renderer2, Input, Output } from '@angular/core';

import { Orientation } from '../common/orientation';

/**
 * Represents the Kendo UI Splitter pane component for Angular.
 */
@Component({
  standalone: false,
  exportAs: 'kendoSplitterPane',
  selector: 'kendo-splitter-pane',
  template: `
    <ng-container *ngIf="!collapsed"><ng-content></ng-content></ng-container>
    <div *ngIf="overlayContent" class="k-splitter-overlay k-overlay"></div>
  `,
})
export class SplitterPaneComponent {
  /**
   * @hidden
   */
  @Input()
  public order: number;

  /**
   * Sets the initial size of the pane.
   * Has to be between the `min` and `max` properties.
   */
  @Input()
  public set size(newSize: string) {
    this._size = newSize;

    const element = this.element.nativeElement;
    this.renderer.setStyle(element, '-ms-flex-preferred-size', newSize);
    this.renderer.setStyle(element, 'flex-basis', newSize);

    if (this.staticPaneClass) {
      this.renderer.addClass(element, 'k-pane-static');
    } else {
      this.renderer.removeClass(element, 'k-pane-static');
    }
  }

  public get size(): string {
    return this._size;
  }

  private _size: string;

  /**
   * Sets the minimum possible size of the pane.
   */
  @Input()
  public min: string;

  /**
   * Sets the maximum possible size of the pane.
   */
  @Input()
  public max: string;

  /**
   * Specifies if the user is allowed to resize the pane and provide space for other panes.
   */
  @Input()
  public resizable: boolean = true;

  /**
   * Specifies if the user is allowed to hide the pane and provide space for other panes.
   */
  @Input()
  public collapsible: boolean = false;

  /**
   * Specifies if overflowing content is scrollable or hidden.
   */
  @Input()
  public scrollable: boolean = true; //todo: check it. if we want to use perfect scrollbar then that value should be set in false.

  /**
   * Specifies if the pane is initially collapsed.
   */
  @Input()
  public collapsed: boolean = false;

  /**
   * @hidden
   */
  @Input()
  public orientation: Orientation = 'horizontal';

  /**
   * @hidden
   */
  @Input()
  public containsSplitter: boolean = false;

  /**
   * @hidden
   */
  @Input()
  public overlayContent: boolean = false;

  /**
   * Fires each time the user resizes the Splitter pane.
   * The event data contains the new pane size.
   * Allows a two-way binding of the pane `size` property.
   */
  @Output()
  public sizeChange: EventEmitter<string> = new EventEmitter<string>();

  /**
   * Fires each time the `collapsed` property changes.
   * The event data contains the new property state.
   * Allows a two-way binding of the `collapsed` pane property.
   */
  @Output()
  public collapsedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @HostBinding('class.k-state-hidden')
  @HostBinding('hidden')
  public get isHidden(): boolean {
    return this.collapsed;
  }

  @HostBinding('style.-ms-flex-order')
  @HostBinding('style.order')
  public get styleOrder(): number {
    return this.order;
  }

  @HostBinding('class.k-pane-flex')
  public get styleDisplayFlex(): boolean {
    return this.containsSplitter;
  }

  @HostBinding('class.k-pane')
  public hostClass: boolean = true;

  @HostBinding('class.k-pane-static')
  public get staticPaneClass(): boolean {
    return (!this.resizable && !this.collapsible) || this.fixedSize;
  }

  @HostBinding('class.k-scrollable')
  public get scrollablePaneClass(): boolean {
    return this.scrollable;
  }

  public get fixedSize(): boolean {
    return this.size && this.size.length > 0;
  }

  constructor(
    private element: ElementRef,
    private renderer: Renderer2,
  ) {}

  /**
   * @hidden
   */
  public get computedSize(): number {
    if (this.orientation === 'vertical') {
      return this.element.nativeElement.offsetHeight;
    } else {
      return this.element.nativeElement.offsetWidth;
    }
  }
}
